/**
 * PagePreviewModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import './PagePreviewModal.css';

const PagePreviewModal = ({ isOpen, onClose, pageId, versionId = null }) => {
  const { showError } = useToast();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isVersionPreview = Boolean(versionId);

  useEffect(() => {
    if (isOpen && (pageId || versionId)) {
      loadPreview();
    } else if (!isOpen) {
      setPageData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pageId, versionId]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setPageData(null);

      if (versionId) {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/content/versions/${versionId}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          showError(response.status === 404 ? 'Version not found' : 'Failed to load version');
          return;
        }

        const data = await response.json();
        const v = data.version;
        setPageData({
          title: v.title,
          description: v.description,
          html: v.html,
          pageType: v.page_type,
          version: v.version_number,
          pageId: v.page_id,
          page_id: v.page_id,
          changed_by: v.changed_by,
          changed_at: v.changed_at,
        });
        return;
      }

      const response = await fetch(`/api/content/pages/${pageId}`, {
        headers: {
          ...(localStorage.getItem('token')
            ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
            : {}),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        showError(response.status === 404 ? 'Page not found' : 'Failed to load page');
        return;
      }

      const data = await response.json();
      setPageData(data);
    } catch (error) {
      console.error('Error loading preview:', error);
      showError(isVersionPreview ? 'Failed to load version' : 'Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const displayPageId = pageData?.pageId || pageData?.page_id;

  return (
    <div
      className="page-preview-modal-overlay page-preview-modal-overlay--above-panel"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="page-preview-modal">
        <div className="page-preview-modal-header">
          <h2>{isVersionPreview ? 'Version Preview' : 'Page Preview'}</h2>
          <button
            className="page-preview-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            {getIcon('x')}
          </button>
        </div>

        <div className="page-preview-modal-content">
          {loading ? (
            <div className="page-preview-loading">
              <div className="loading-spinner"></div>
              <p>{isVersionPreview ? 'Loading version...' : 'Loading page...'}</p>
            </div>
          ) : pageData ? (
            <>
              <div className="page-preview-header">
                {pageData.title && (
                  <h1 className="page-preview-title">{pageData.title}</h1>
                )}
                {pageData.description && (
                  <p className="page-preview-description">{pageData.description}</p>
                )}
                <div className="page-preview-meta">
                  <span className="page-preview-type">{pageData.pageType || 'general'}</span>
                  <span className="page-preview-version">Version {pageData.version}</span>
                  {displayPageId && (
                    <span className="page-preview-id">ID: {displayPageId}</span>
                  )}
                  {isVersionPreview && pageData.changed_by && (
                    <span className="page-preview-id">
                      {getIcon('user')} {pageData.changed_by}
                    </span>
                  )}
                  {isVersionPreview && pageData.changed_at && (
                    <span className="page-preview-id">
                      {getIcon('calendar')}{' '}
                      {new Date(pageData.changed_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              <div className="page-preview-content">
                <div
                  className="page-preview-html"
                  dangerouslySetInnerHTML={{ __html: pageData.html || '' }}
                />
              </div>
            </>
          ) : (
            <div className="page-preview-error">
              <h3>{isVersionPreview ? 'Version Not Found' : 'Page Not Found'}</h3>
              <p>
                {isVersionPreview
                  ? 'This version snapshot could not be loaded.'
                  : "The page you're looking for doesn't exist or has been removed."}
              </p>
            </div>
          )}
        </div>

        <div className="page-preview-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {pageData && !isVersionPreview && displayPageId && (
            <a
              href={`/page/${displayPageId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              {getIcon('external-link')} Open in New Tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagePreviewModal;
